"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useAuth } from "../lib/auth";
import { useIsMobile } from "../lib/use-mobile";
import { tourDayForProfile } from "../lib/trial-tour";

const BAR_HEIGHT = 44;

// Always-on trial countdown. Fixed to viewport bottom, full width, quiet.
// Renders during active trial (days 1-14) and after lapse.
// Desktop-only for this pass.
export default function TrialStickyBottom() {
  const { user, profile, isLapsed } = useAuth();
  const isMobile = useIsMobile();

  const day = tourDayForProfile(profile);
  const show = Boolean(user)
    && profile
    && profile.role !== "owner"
    && !isMobile
    && (day !== null || isLapsed);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (show) {
      document.documentElement.style.setProperty("--trial-sticky-height", `${BAR_HEIGHT}px`);
    } else {
      document.documentElement.style.removeProperty("--trial-sticky-height");
    }
    return () => {
      if (typeof document !== "undefined") {
        document.documentElement.style.removeProperty("--trial-sticky-height");
      }
    };
  }, [show]);

  if (!show) return null;

  const daysLeft = day !== null ? 15 - day : 0;
  const label = isLapsed || day === null
    ? "Your trial is up"
    : `Trial Day ${day} of 14 · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
  const ctaLabel = isLapsed ? "Resubscribe · $9/mo" : "Subscribe · $9/mo";

  return (
    <div
      role="complementary"
      aria-label="Trial status"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: BAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 var(--space-6)",
        background: "var(--color-bg)",
        borderTop: "1px solid var(--color-border-light)",
        fontFamily: "var(--font-primary)",
        zIndex: 50,
      }}
    >
      <span style={{
        fontSize: "var(--font-size-xs)",
        color: "var(--color-text-secondary)",
        fontWeight: "var(--font-weight-medium)",
        letterSpacing: "0.03em",
      }}>
        {label}
      </span>
      <Link
        href="/settings/billing"
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "var(--space-1-5) var(--space-3)",
          background: "var(--color-text)",
          color: "var(--color-bg)",
          borderRadius: "var(--radius-sm)",
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-semibold)",
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        {ctaLabel}
      </Link>
    </div>
  );
}
